import { Country, Currency, LanguageCode, OrganizationRecord, TimeZone } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm } from 'noddi-ui-web';
import { useEffect } from 'react';
import { z } from 'zod';
import { TanstackCurrencySelect } from '../../form/tanstack/features/Currency/TanstackCurrencySelect';
import { TanstackLanguageCodeSelect } from '../../form/tanstack/features/Language/TanstackLanguageCodeSelect';
import { TanstackCountrySelect } from '../../form/tanstack/features/Location/TanstackCountrySelect';
import { TanstackTimeZoneSelect } from '../../form/tanstack/features/Location/TanstackTimeZoneSelect';
import { invalidateOrganizationQueries } from './invalidateOrganizationQueries';

const FORM_ID = 'edit-organization-settings-form';

const COUNTRY_VALUES = Object.values(Country) as [Country, ...Country[]];
const CURRENCY_VALUES = Object.values(Currency) as [Currency, ...Currency[]];
const LANGUAGE_CODE_VALUES = Object.values(LanguageCode) as [LanguageCode, ...LanguageCode[]];
const TIME_ZONE_VALUES = Object.values(TimeZone) as [TimeZone, ...TimeZone[]];

const validationSchema = z.object({
  country: z.enum(COUNTRY_VALUES),
  currency: z.enum(CURRENCY_VALUES),
  languageCode: z.enum(LANGUAGE_CODE_VALUES),
  timeZone: z.enum(TIME_ZONE_VALUES)
});

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  organization: OrganizationRecord;
  organizationId: number;
};

export const EditOrganizationSettingsSheet = ({ open, onClose, organization, organizationId }: Props) => {
  const { mutateAsync: patchOrganization } = noddiAsync.usePatch({
    type: URLKeys.patchOrganization,
    queryConfig: {
      onSuccess: () => {
        invalidateOrganizationQueries(organizationId);
        onClose();
      }
    }
  });

  const getDefaultValues = (): FormValues => ({
    country: organization.country.code,
    currency: organization.currency.value,
    languageCode: organization.language.value,
    timeZone: organization.timeZone.value
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(),
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      await patchOrganization({
        id: organizationId,
        country: value.country,
        currency: value.currency,
        language: value.languageCode,
        timeZone: value.timeZone
      });
    }
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getDefaultValues is a new closure each render and would reset on every render; reset only on open or when the fields it reads change
  }, [
    open,
    form,
    organization.country.code,
    organization.currency.value,
    organization.language.value,
    organization.timeZone.value
  ]);

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit settings'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        <form
          id={FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='flex flex-col gap-5'>
            <form.AppField name='country'>{() => <TanstackCountrySelect />}</form.AppField>
            <form.AppField name='currency'>{() => <TanstackCurrencySelect />}</form.AppField>
            <form.AppField name='languageCode'>{() => <TanstackLanguageCodeSelect />}</form.AppField>
            <form.AppField name='timeZone'>{() => <TanstackTimeZoneSelect />}</form.AppField>
          </div>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
